package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.htmlagent.site.components.widgets.prefersReducedMotion
import com.htmlagent.site.components.widgets.reveal
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.Motion
import com.htmlagent.site.theme.StatBodyStyle
import com.htmlagent.site.theme.StatCardStyle
import com.htmlagent.site.theme.StatGridStyle
import com.htmlagent.site.theme.StatLabelStyle
import com.htmlagent.site.theme.StatValueStyle
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import kotlin.js.json

/**
 * The four figures, and the one place on the page where a number is the argument.
 *
 * Every one is a verifiable fact stated in the reader's terms rather than ours: nothing to pay,
 * no templates behind the page, fourteen validator checks, one file to download. They are the
 * reason the band exists — a landing that only describes itself is asking to be believed.
 */
private data class Figure(val value: String, val label: String, val body: String)

/**
 * What the generated page is made of: four counts on ink.
 *
 * Ink because this is the middle of the page and the eye needs a hard change to keep reading,
 * and because a figure set in the accent reads loudest against a dark surface.
 */
@Composable
fun LandingStats() {
    val t = LocalStrings.current
    val figures = listOf(
        Figure("0", t.landing.statPriceLabel, t.landing.statPriceBody),
        Figure("0", t.landing.statTemplatesLabel, t.landing.statTemplatesBody),
        Figure("14", t.landing.statChecksLabel, t.landing.statChecksBody),
        Figure("1", t.landing.statFileLabel, t.landing.statFileBody),
    )

    Band(SECTION_STATS, ink = true) {
        SectionHead(
            t.landing.statsEyebrow,
            t.landing.statsTitle,
            ink = true,
            headingId = headingId(SECTION_STATS),
        )

        Div(StatGridStyle.toModifier().toAttrs()) {
            figures.forEachIndexed { position, figure ->
                Div(StatCardStyle.toModifier().reveal(position * STAGGER_MS).floatWhileHovered().toAttrs()) {
                    Div(StatValueStyle.toModifier().toAttrs()) { Text(figure.value) }
                    Div(StatLabelStyle.toModifier().toAttrs()) { Text(figure.label) }
                    Div(StatBodyStyle.toModifier().toAttrs()) { Text(figure.body) }
                }
            }
        }

        LandingTip()
    }
}

/**
 * Floats the card while the pointer rests on it: rise, tilt a little, come back. The same gesture
 * as the hero's mock-up, at a third of its amplitude, since these are four abreast.
 *
 * It is driven from here and not by a `:hover` animation because of the way out. A CSS animation
 * is removed the instant the pointer leaves and the card dropped up to 7px in a single frame;
 * this one reads where the float had got to and eases back from there.
 */
@Composable
private fun Modifier.floatWhileHovered(): Modifier {
    val card = remember { HoverFloat() }
    // The element comes with the event: a `ref` here would replace the one `reveal` sets.
    return onMouseEnter { card.start(it.nativeEvent.currentTarget as? Element) }
        .onMouseLeave { card.settle(it.nativeEvent.currentTarget as? Element) }
}

/** The running float of one card. */
private class HoverFloat {
    private var running: dynamic = null

    fun start(target: Element?) {
        if (target == null || prefersReducedMotion()) return
        running?.cancel()
        running = target.asDynamic().animate(
            arrayOf(
                json("transform" to "none"),
                json("transform" to "translateY(-7px) rotate(-0.7deg)"),
                json("transform" to "none"),
            ),
            json("duration" to FLOAT_MS, "iterations" to Double.POSITIVE_INFINITY, "easing" to Motion.Breathe),
        )
    }

    fun settle(target: Element?) {
        if (target == null) return
        val float = running ?: return
        val from = window.getComputedStyle(target).transform
        float.cancel()
        running = target.asDynamic().animate(
            arrayOf(json("transform" to from), json("transform" to "none")),
            json("duration" to SETTLE_MS, "easing" to Motion.Curve),
        )
    }
}

/**
 * How long a card takes to rise and come back. Shorter than the hero's float: that one is
 * scenery, this one starts when you arrive and has to have moved before you leave.
 */
private const val FLOAT_MS = 3200

/** How long a card takes to come back to rest once the pointer leaves it. */
private const val SETTLE_MS = 450
